import { useEffect, useState } from "react";

import { BrandModal } from "@/components/common/BrandModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { TermChip } from "@/components/filters/TermChip";
import type { TdmKeyword } from "@/hooks/useTdmFilters";

/**
 * Gestión de los términos del TDM. A diferencia de la tira en línea, aquí el
 * clic en la pill edita el texto. Los cambios se aplican al guardar.
 */
export function TdmKeywordsDialog({
  keywords,
  open,
  onOpenChange,
  onSave,
}: {
  keywords: TdmKeyword[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (next: TdmKeyword[]) => void;
}) {
  const [draft, setDraft] = useState<TdmKeyword[]>(keywords);
  const [term, setTerm] = useState("");

  useEffect(() => {
    if (open) {
      setDraft(keywords);
      setTerm("");
    }
  }, [open]);

  // Tal cual se escribió: el espacio de borde es intencional.
  function add() {
    if (!term.trim() || draft.some((k) => k.term === term)) return;
    setDraft((prev) => [...prev, { term, active: true }]);
    setTerm("");
  }

  function rename(index: number, next: string) {
    if (draft.some((k) => k.term === next)) return;
    setDraft((p) => p.map((k, i) => (i === index ? { ...k, term: next } : k)));
  }

  return (
    <BrandModal
      open={open}
      onOpenChange={onOpenChange}
      title="Gestionar recursos a buscar"
      description={
        <>
          Cada término se busca en la descripción de los recursos de Materiales y
          Subcontratos; una licitación entra si coincide con{" "}
          <strong>alguno</strong>. Corta a la raíz para cubrir las variantes
          ("asf" cubre asfalto, asfáltica y asfaltado). Haz clic en un término
          para corregirlo.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onSave(draft);
              onOpenChange(false);
            }}
          >
            Guardar cambios
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold">Término*</Label>
        <div className="flex gap-3">
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="flex-1"
          />
          <Button variant="secondary" onClick={add} disabled={!term.trim()}>
            + Agregar
          </Button>
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-bold">Tus términos:</p>
        {draft.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sin términos no hay búsqueda: el dashboard cae a "asf" por defecto.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {draft.map((k, i) => (
              <TermChip
                key={i}
                term={k.term}
                active={k.active}
                onRemove={() => setDraft((p) => p.filter((_, x) => x !== i))}
                onRename={(next) => rename(i, next)}
                onToggle={() =>
                  setDraft((p) =>
                    p.map((x, idx) =>
                      idx === i ? { ...x, active: !x.active } : x,
                    ),
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </BrandModal>
  );
}
